/**
 * Builds the {{token}} -> value map used to fill document boilerplate.
 * Combines the record (customer, address, dates), document_data (rates,
 * ownership, snow, term), and RBC branding into computed clauses.
 */
import type { DocumentData } from '../types/agreement'
import { RBC_BRANDING } from './proposalDocumentTemplates'
import { formatCurrency } from './documentCompensation'

export interface DocumentRecord {
  phase: 'proposal' | 'contract'
  serviceType?: string
  title?: string
  customerCompany?: string
  contactName?: string
  contactFirstName?: string
  recipientAddress?: string
  projectAddress?: string
  subject?: string
  date?: string | null
  validUntil?: string | null
  startDate?: string | null
  endDate?: string | null
  contractType?: string
  paymentTerms?: number | string
  billingFrequency?: string
  progressMilestones?: Array<{ label: string; percentage: number; amount: number }>
}

export function formatLongDate(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function additionalWorkUnitPhrase(unit: string): string {
  switch ((unit || '').trim()) {
    case 'per_hour':
      return 'per hour for each laborer'
    case 'per_apartment':
      return 'per apartment'
    case 'per_unit':
      return 'per unit'
    case 'flat':
    case 'flat_rate':
      return 'as a flat rate'
    default:
      return (unit || '').replace(/_/g, ' ')
  }
}

export function buildDocumentTokens(
  record: DocumentRecord,
  documentData: DocumentData
): Record<string, string> {
  const dd = documentData || {}
  const suppliesProvider = dd.suppliesBy === 'customer' ? 'Customer' : RBC_BRANDING.companyName
  const equipmentProvider = dd.equipmentBy === 'customer' ? 'Customer' : RBC_BRANDING.companyName

  // Additional / special-services labor clause
  let additionalWorkClause = ''
  if (dd.additionalWork && Number(dd.additionalWork.rate) > 0) {
    additionalWorkClause = `All additional labor shall be billed at the agreed-upon rate of ${formatCurrency(
      dd.additionalWork.rate
    )} ${additionalWorkUnitPhrase(dd.additionalWork.unit)}.`
  }

  // Invoicing clause
  let invoicingClause: string
  const terms =
    record.paymentTerms !== undefined && record.paymentTerms !== ''
      ? `Net ${record.paymentTerms} days`
      : 'Net 15 days'
  if ((record.billingFrequency || '') === 'monthly' && record.contractType === 'recurring') {
    invoicingClause =
      'All invoicing will be itemized according to monthly work or special tasks. Invoicing will be on the 1st of each month and payment will be deducted automatically on the 5th of the month from the payment method on file.'
  } else if (record.billingFrequency === 'progress' && record.progressMilestones?.length) {
    const schedule = record.progressMilestones
      .map((m) => `${m.label}: ${m.percentage}% (${formatCurrency(m.amount)})`)
      .join('; ')
    invoicingClause = `Payment will be made according to the following progress billing schedule: ${schedule}. Payment terms: ${terms}.`
  } else {
    invoicingClause = `Invoices will be itemized and submitted promptly to coincide with the completion of all, or part, of the services. Payment terms: ${terms}.`
  }

  // Service schedule clause
  const scheduleClause =
    record.contractType === 'recurring'
      ? 'Services described in this program will be performed as shown in the attached Work Specifications on a recurring schedule.'
      : 'Tasks described in the attached specifications will be scheduled for, and performed at, mutually agreed upon date(s) and time(s) yet to be determined.'

  // Snow clause
  let snowClause = ''
  if (dd.snow && dd.snow.enabled) {
    const t = dd.snow.thresholdInches || 8
    snowClause = `Snow ${t} inches and up – ${formatCurrency(dd.snow.overRate)} per snow. Less than ${t} inches – ${formatCurrency(
      dd.snow.underRate
    )} per snow.`
    if (dd.snow.includedCount && dd.snow.includedCount > 0) {
      snowClause += ` The first ${dd.snow.includedCount} snowfall${dd.snow.includedCount === 1 ? '' : 's'} of less than ${t} inches per season are included in the base price.`
    }
  }

  // Term clause (proposal = validity, contract = duration / auto-renew)
  let termClause: string
  if (record.phase === 'proposal') {
    termClause = record.validUntil
      ? `This proposal is valid until ${formatLongDate(record.validUntil)}.`
      : 'This proposal shall be valid for a period of sixty (60) days from the date above.'
  } else if (dd.autoRenew) {
    termClause =
      'The term of this agreement shall be for a period of one (1) year and shall automatically renew for additional one (1) year periods on the anniversary date of this agreement.'
  } else if (record.startDate && record.endDate) {
    termClause = `The term of this agreement shall run from ${formatLongDate(record.startDate)} to ${formatLongDate(
      record.endDate
    )}.`
  } else {
    termClause = 'The term of this agreement shall be for a period of one (1) year.'
  }

  const agreementDate = record.date ? formatLongDate(record.date) : '______ / ______ / 20___'

  const siteLine = (record.projectAddress || '').trim()
  const subjectBase = (record.subject || '').trim()
  const mailingFirstLine = (record.recipientAddress || '').split('\n')[0]?.trim() || ''
  const mailingComma = mailingFirstLine.indexOf(',')
  const mailingStreet =
    mailingComma > 0 ? mailingFirstLine.slice(0, mailingComma).trim() : mailingFirstLine
  const subjectStreet = siteLine || mailingStreet
  const subjectLine =
    subjectBase && subjectStreet
      ? `${subjectBase} Project: ${subjectStreet}`
      : subjectBase || subjectStreet

  const customerMailing = (record.recipientAddress || '').trim()
  const siteFallback = (record.projectAddress || '').trim()
  const customerAddress = customerMailing || siteFallback

  return {
    companyName: RBC_BRANDING.companyName,
    companyAddress: RBC_BRANDING.companyAddress,
    repName: RBC_BRANDING.repName,
    repPhone: RBC_BRANDING.repPhone,
    repEmail: RBC_BRANDING.repEmail,
    customerCompany: record.customerCompany || record.contactName || 'Customer',
    customerName: record.contactName || '',
    contactFirstName: record.contactFirstName || record.contactName || '',
    customerAddress,
    projectAddress: siteLine,
    subjectLine,
    date: formatLongDate(record.date),
    agreementDate,
    suppliesProvider,
    equipmentProvider,
    additionalWorkClause,
    invoicingClause,
    scheduleClause,
    snowClause,
    termClause,
  }
}
